"use client";

import React, {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from "react";
import { LINK_POSITION_COMPONENT_TAB, fireLinkClick } from "../../services/analytics";
import { pageHref, viewSwitchLinkName } from "./lineup-analytics.shared";
import {
  SearchableSignatureGrid,
  type SearchableSignatureGridProps
} from "./signature-grid.client";
import { SortRadios, type SortOption } from "./sort-radios.client";
import { DEFAULT_OPEN_ATTRIBUTE } from "./disclosure.shared";
import { useLineupMarketRows } from "./lineup-market.client";
import { withMarketChannels } from "./lineup-market.shared";
import styles from "./lineup-view.module.css";
import { StickyOffsetScope } from "./sticky-offset.client";
import { ViewToggle, type ViewToggleOption } from "./view-toggle.client";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Which view and which sort the lineup is currently showing — VT3, VT4, GH4. the context argument,
 * made here rather than borrowed.
 */

interface LineupViewContextValue {
  /** `signature` | `genre` — the organism's own `ChannelLineupView` values,
   *  which is what the authored option `value`s have to be. */
  view: string;
  /** GH4's current sort, always one of the rendered options' values. */
  sort: string;
  /** VT3 — reports the newly chosen view. A no-op for the view already shown. */
  chooseView: (value: string) => void;
  chooseSort: (value: string) => void;
}

interface LineupViewStore {
  get: () => LineupViewContextValue;
  subscribe: (listener: () => void) => () => void;
}

/* A store, not a context value that changes. */
function createLineupViewStore(initialView: string, initialSort: string): LineupViewStore {
  const listeners = new Set<() => void>();
  let snapshot: LineupViewContextValue;
  const publish = (view: string, sort: string) => {
    snapshot = {
      view,
      sort,
      chooseView,
      chooseSort
    };
    listeners.forEach(listener => listener());
  };
  function chooseView(next: string) {
    /* VT3 — "choosing the option that is already selected does nothing". A native radio group never
       fires `change` for the checked option, so `ViewToggle` cannot reach this; the guard makes the
       rule true for any other caller. */
    if (next !== snapshot.view) {
      publish(next, snapshot.sort);
    }
  }
  function chooseSort(next: string) {
    if (next !== snapshot.sort) {
      publish(snapshot.view, next);
    }
  }
  snapshot = {
    view: initialView,
    sort: initialSort,
    chooseView,
    chooseSort
  };

  return {
    get: () => snapshot,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}

const LineupViewContext = createContext<LineupViewStore | null>(null);

export interface LineupViewProviderProps {
  /**
   * VT2's answer, and only the first paint's: once the reader touches the toggle this value no
   * longer describes what is on screen. Resolved on the server by `resolveInitialView`.
   */
  initialView: string;
  /**
   * GH4's load-time selection, and only the first paint's: once the reader touches the radios this
   * value no longer describes what is on screen. It is not a reset target any more — `chooseView`
   * records why VT4's reset was removed.
   */
  initialSort: string;
  children: React.ReactNode;
}

function LineupViewProviderImpl({
  initialView,
  initialSort,
  children
}: LineupViewProviderProps) {
  /* Seeded once: `initialView`/`initialSort` describe only the first paint. */
  const [store] = useState(() => createLineupViewStore(initialView, initialSort));

  return <LineupViewContext.Provider value={store}>{children}</LineupViewContext.Provider>;
}
export const LineupViewProvider = withSafeRender(LineupViewProviderImpl, "LineupViewProvider");

/**
 * Throws outside the provider, where `useLineupSearchSelection` only warns. The difference is what
 * the two failures look like: a view reading an inert selection still renders the whole lineup, so
 * warning and carrying on leaves a usable page.
 */
export function useLineupViewState(): LineupViewContextValue {
  const context = useContext(LineupViewContext);
  if (!context) {
    throw new Error(
      "[LineupView] must be rendered inside <LineupViewProvider>."
    );
  }
  return useSyncExternalStore(context.subscribe, context.get, context.get);
}

export interface LineupViewToggleProps {
  /** VT1 — the content team's options, in its own order. Fewer than two
   *  usable ones and `ViewToggle` renders nothing at all; VT2 still chose the
   *  view, there is just no control to show for it. */
  options?: ViewToggleOption[];
  /** VT7 — the group's accessible name, authored. */
  aria_label?: string;
  className?: string;
}

/** What goes in the root's `viewToggleSlot` — `ViewToggle` bound to the live
 *  view. The smallest thing that can read it, the same shape
 *  `SearchableGridHeader` uses for `searchActive`. */
function LineupViewToggleImpl({
  options,
  aria_label: ariaLabel,
  className
}: LineupViewToggleProps) {
  const { view, chooseView } = useLineupViewState();

  /** AN8 — the switch reports a `link_click` at `component_tab`, named after the view switched to. */
  const handleChange = (next: string) => {
    const chosen = (options ?? []).find(option => option?.value === next);
    /* An option the toggle does not carry has no wording to report, and
       `Show Channel By ` with nothing after it is a row nobody can read. The
       view still changes — reporting is never allowed to decide that. */
    if (chosen && chosen.label) {
      fireLinkClick({
        linkDestination: pageHref(),
        linkName: viewSwitchLinkName(chosen.label),
        linkPosition: LINK_POSITION_COMPONENT_TAB
      });
    }

    chooseView(next);
  };

  return (
    <ViewToggle
      ariaLabel={ariaLabel}
      className={className}
      onChange={handleChange}
      options={options}
      value={view}
    />
  );
}
export const LineupViewToggle = withSafeRender(LineupViewToggleImpl, "LineupViewToggle");

export interface LineupSortRadiosProps {
  /** GH4 — the content team's options. Nothing usable draws no control. */
  options?: SortOption[];
  /** The group's accessible name, authored. Not the visible sort wording,
   *  which the header draws beside it. */
  aria_label?: string;
  className?: string;
}

/** What goes in `GridHeaderProps.sortSlot` — `SortRadios` bound to the live
 *  sort. */
function LineupSortRadiosImpl({
  options,
  aria_label: ariaLabel,
  className
}: LineupSortRadiosProps) {
  const { sort, chooseSort } = useLineupViewState();

  return (
    <SortRadios
      ariaLabel={ariaLabel}
      className={className}
      onChange={chooseSort}
      options={options}
      value={sort}
    />
  );
}
export const LineupSortRadios = withSafeRender(LineupSortRadiosImpl, "LineupSortRadios");

/** The Signature grid on the live sort. */
function LineupSortedGridImpl(props: Omit<SearchableSignatureGridProps, "sort">) {
  const { sort } = useLineupViewState();
  /* The radio repaints at once; the regroup of every row renders after it and
     can be interrupted by the next click. */
  const shownSort = useDeferredValue(sort);
  const marketRows = useLineupMarketRows();
  const channels = useMemo(() => withMarketChannels(props.channels, marketRows), [props.channels, marketRows]);

  /* Buys a bail-out on every context change that is not a sort — a view switch re-renders this
     consumer, and without it all ~700 rows with it. `props` keeps its identity: the parent is
     server-rendered and never re-renders on the client. */
  const grid = useMemo(
    () => <SearchableSignatureGrid {...props} channels={channels} sort={shownSort} />,
    [props, channels, shownSort]
  );

  return <div aria-busy={sort !== shownSort || undefined} className={styles.pending}>{grid}</div>;
}
export const LineupSortedGrid = withSafeRender(LineupSortedGridImpl, "LineupSortedGrid");

export interface LineupSelectedViewProps {
  /** The Signature packages view — its header and grid, already built. */
  signatureSlot: React.ReactNode;
  /** The Genre Packs view, already built. */
  genreSlot: React.ReactNode;
}

/**
 * What goes in the root's `selectedViewSlot` — VT3's switch, with both views handed in already
 * rendered so nothing new crosses the boundary.
 */
function LineupSelectedViewImpl({ signatureSlot, genreSlot }: LineupSelectedViewProps) {
  const { view } = useLineupViewState();
  const shownView = useDeferredValue(view);
  const isGenre = shownView === "genre";
  const signatureRef = useRef<HTMLDivElement>(null);

  /* Adjusting state during render, so a newly chosen view mounts in the same
     deferred render that shows it — no extra commit. */
  const [mounted, setMounted] = useState({
    genre: isGenre,
    signature: !isGenre
  });
  if (!mounted[isGenre ? "genre" : "signature"]) {
    setMounted({
      ...mounted,
      [isGenre ? "genre" : "signature"]: true
    });
  }

  useEffect(() => {
    const signature = signatureRef.current;
    if (!isGenre || !signature) {
      return undefined;
    }
    /* The hidden view keeps its box (content-visibility), so the reset's
       `toggle` events are stopped here before `SectionScrollKeeper` measures
       a section nobody can see. `toggle` does not bubble but does capture. */
    const swallow = (event: Event) => event.stopPropagation();
    signature.addEventListener("toggle", swallow, true);
    signature.querySelectorAll("details").forEach(details => {
      details.open = details.hasAttribute(DEFAULT_OPEN_ATTRIBUTE);
    });
    return () => signature.removeEventListener("toggle", swallow, true);
  }, [isGenre]);

  return (
    <StickyOffsetScope>
      <div aria-busy={view !== shownView || undefined} className={styles.pending}>
        {mounted.signature && (
          <div className={`${styles.view} ${styles.signatureView}`} hidden={isGenre} ref={signatureRef} {...inertWhen(isGenre)}>
            {signatureSlot}
          </div>
        )}
        {mounted.genre && (
          <div className={`${styles.view} ${styles.genreView}`} hidden={!isGenre} {...inertWhen(!isGenre)}>
            {genreSlot}
          </div>
        )}
      </div>
    </StickyOffsetScope>
  );
}
export const LineupSelectedView = withSafeRender(LineupSelectedViewImpl, "LineupSelectedView");

/* React 18 has no `inert` prop; an empty string renders the boolean attribute.
   It keeps the hidden view out of the tab order and the accessibility tree now
   that `content-visibility` (not `display: none`) does the hiding. */
function inertWhen(on: boolean) {
  return (on ? { inert: "" } : {}) as React.HTMLAttributes<HTMLDivElement>;
}

export default LineupViewProvider;
